import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  FeedbackActivity,
  FeedbackDuplicatePage,
  FeedbackAssigneePage,
  FeedbackPlanningInput,
  FeedbackEntry,
  FeedbackKind,
  FeedbackImplementationInput,
  FeedbackPage,
  FeedbackPostponementInput,
  FeedbackScreenshot,
  FeedbackSortDirection,
  FeedbackSortKey,
  FeedbackStatus,
} from './types'
import { useFeedbackApi } from '../feedback-api'

/**
 * Datenzugriff der Plattformfunktion.
 *
 * Der Client verwendet ausdrücklich `/api/feedback`, hängt das Zugriffstoken
 * an und übersetzt Fehlerantworten zentral.
 */

export interface FeedbackListParams {
  assignment?: 'mine' | 'unassigned'
  page: number
  pageSize: number
  status?: FeedbackStatus
  art?: FeedbackKind
  text?: string
  modul?: string
  ersteller?: string
  nurUngelesen?: boolean
  angelegtVon?: string
  angelegtBis?: string
  sortKey: FeedbackSortKey
  sortDirection: FeedbackSortDirection
}

/** Query-Schlüssel an einer Stelle: verhindert, dass Caches auseinanderlaufen. */
export const feedbackKeys = {
  root: ['feedback'] as const,
  all: ['feedback', 'eintraege'] as const,
  list: (params: FeedbackListParams) => [...feedbackKeys.all, 'list', params] as const,
  detail: (id: string) => [...feedbackKeys.all, 'detail', id] as const,
  screenshot: (id: string) => [...feedbackKeys.all, 'screenshot', id] as const,
  activities: (id: string) => [...feedbackKeys.all, 'activities', id] as const,
  filterOptions: () => [...feedbackKeys.all, 'filter-options'] as const,
}

export function useFeedbackDuplicates(id: string, page: number) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.all, 'duplicates', id, page],
    queryFn: ({ signal }) =>
      api.get<FeedbackDuplicatePage>(`/eintraege/${id}/duplicates`, {
        query: { page, pageSize: 20 },
        signal,
      }),
  })
}

export function useFeedbackDuplicateTargets(id: string, search: string, page: number) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.all, 'duplicate-targets', id, search, page],
    queryFn: ({ signal }) =>
      api.get<FeedbackDuplicatePage>(`/eintraege/${id}/duplicate-targets`, {
        query: { search, page, pageSize: 20 },
        signal,
      }),
  })
}

export function useFeedbackDuplicateChange(id: string) {
  const api = useFeedbackApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { duplicateOfId: string | null; duplicateVersion: string }) =>
      api.put<FeedbackEntry>(`/eintraege/${id}/duplicate`, input),
    onSuccess: (saved) => {
      client.setQueryData(feedbackKeys.detail(id), saved)
      void client.invalidateQueries({ queryKey: feedbackKeys.root })
    },
  })
}

export function useFeedbackPublishProgress(id: string) {
  const api = useFeedbackApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { note: string | null; progressVersion: string }) =>
      api.put<FeedbackEntry>(`/eintraege/${id}/progress`, input),
    onSuccess: (saved) => {
      client.setQueryData(feedbackKeys.detail(id), saved)
      void client.invalidateQueries({ queryKey: feedbackKeys.root })
    },
  })
}

export function useFeedbackAssignees(search: string, page: number) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.root, 'assignees', search, page],
    queryFn: ({ signal }) =>
      api.get<FeedbackAssigneePage>('/assignees', {
        query: { search, page, pageSize: 20 },
        signal,
      }),
  })
}

export function useFeedbackPlanning(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: FeedbackPlanningInput) =>
      api.put<FeedbackEntry>(`/eintraege/${id}/planung`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.root })
    },
  })
}

export function useFeedbackList(params: FeedbackListParams) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.list(params),
    queryFn: ({ signal }) =>
      api.get<FeedbackPage>('/eintraege', {
        query: { ...params },
        signal,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  })
}

/** Kleine Abfrage für Hinweise außerhalb der Feedback-Ansicht, etwa die Navigation. */
export function useFeedbackUnreadCount(enabled: boolean) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: [...feedbackKeys.all, 'unread-count'],
    enabled,
    queryFn: ({ signal }) =>
      api.get<Pick<FeedbackPage, 'unreadCount'>>('/eintraege', {
        query: { page: 1, pageSize: 1, nurUngelesen: true },
        signal,
      }),
    refetchInterval: 30_000,
  })
}

export function useFeedback(id: string | undefined) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.detail(id ?? ''),
    enabled: Boolean(id),
    queryFn: ({ signal }) => api.get<FeedbackEntry>(`/eintraege/${id}`, { signal }),
    refetchInterval: 30_000,
  })
}

export function useFeedbackScreenshot(id: string | undefined, enabled = true) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.screenshot(id ?? ''),
    enabled: Boolean(id) && enabled,
    queryFn: ({ signal }) =>
      api.get<FeedbackScreenshot>(`/eintraege/${id}/screenshot`, { signal }),
  })
}

export function useFeedbackAktivitaeten(id: string | undefined) {
  const api = useFeedbackApi()
  return useQuery({
    queryKey: feedbackKeys.activities(id ?? ''),
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      api.get<FeedbackActivity[]>(`/eintraege/${id}/aktivitaeten`, { signal }),
    refetchInterval: 30_000,
  })
}

export function useFeedbackStatusAendern(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (status: FeedbackStatus) =>
      api.patch<FeedbackEntry>(`/eintraege/${id}/status`, { status }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackBereichAendern(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (modulId: string | null) =>
      api.patch<FeedbackEntry>(`/eintraege/${id}/bereich`, { modulId }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackKommentarHinzufuegen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) =>
      api.post<FeedbackActivity>(`/eintraege/${id}/kommentare`, { text }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.root })
    },
  })
}

export function useFeedbackUmsetzungHinzufuegen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: FeedbackImplementationInput) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/umsetzungen`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackInBearbeitungSetzen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (text: string) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/bearbeitungen`, { text }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackAblehnen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (grund: string) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/ablehnungen`, { grund }),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackZurueckstellen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (input: FeedbackPostponementInput) =>
      api.post<FeedbackEntry>(`/eintraege/${id}/zurueckstellungen`, input),
    onSuccess: (saved) => {
      queryClient.setQueryData(feedbackKeys.detail(id), saved)
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.activities(id) })
    },
  })
}

export function useFeedbackAlsGelesenMarkieren(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<void>(`/eintraege/${id}/gelesen`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.root }),
  })
}

export function useAlleFeedbacksAlsGelesenMarkieren() {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.post<void>('/eintraege/alle-gelesen', {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: feedbackKeys.all }),
  })
}

export function useFeedbackLoeschen(id: string) {
  const api = useFeedbackApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => api.delete<void>(`/eintraege/${id}`),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: feedbackKeys.detail(id) })
      queryClient.removeQueries({ queryKey: feedbackKeys.screenshot(id) })
      queryClient.removeQueries({ queryKey: feedbackKeys.activities(id) })
      void queryClient.invalidateQueries({ queryKey: feedbackKeys.all })
    },
  })
}
